import { ImagePoint } from "../../../data/image-point";
import { InventoryZone } from "../inventory-zone";

export interface InventoryState {
	counters: InventoryStateCounters;
	generalContents: InventoryStateContents;
	sectionContents: InventoryStateSectionContents[];
}

export interface InventoryStateCounters {
	confirmed: number;
	missing: number;
	misplaced: number;
	unassigned: number;
}

/**
 * The status of an inventory item from the perspective
 * of a single section
 */
export type InventorySectionRelativeStatus =
	| {
			// The asset is assigned to this section,
			// and located in this section
			type: "confirmed";
	  }
	| {
			// The asset is not assigned to this section,
			// located in this section, and optionally
			// assigned to a different section
			type: "unassigned";
			belongsIn: string | null;
	  }
	| {
			// The asset is assigned to this section,
			// and is either not located in any section,
			// or located in a different section
			type: "missing";
			foundIn: string | null;
	  };

/// An asset as found in an inventory section
export interface InventoryStateAsset {
	id: string;
	name: string;
	status: InventorySectionRelativeStatus;
}

/// A sub-section of an inventory capable of being assigned
/// and holding assets.
export interface InventoryStateContents {
	/// The IDs of the assets assigned to this section.
	assignments: ReadonlySet<string>;
	/// The assets relevant to this section.
	assets: InventoryStateAsset[];
}

/// A registered inventory section of an inventory zone
export type InventoryStateSectionContents = InventoryStateContents & {
	id: string;
	name: string;
	bounds: ImagePoint[] | null;
};

export const INVENTORY_STATE: InventoryState = {
	counters: {
		confirmed: 2,
		missing: 1,
		misplaced: 1,
		unassigned: 1,
	},
	generalContents: {
		assignments: new Set<string>(),
		assets: [
			{
				id: "a5",
				name: "Spare SSD",
				status: { type: "confirmed" },
			},
			{
				id: "a6",
				name: "The screwdriver you can never find",
				status: { type: "missing", foundIn: null },
			},
			{
				id: "a7",
				name: "The screwdriver you never need",
				status: { type: "unassigned", belongsIn: null },
			},
		],
	},
	sectionContents: [
		{
			id: "c1",
			name: "Server Rack A",
			assignments: new Set(["a1", "a2"]),
			assets: [
				{
					id: "a1",
					name: "Switch 24p",
					status: { type: "confirmed" },
				},
				{
					id: "a3",
					name: "Firewall",
					status: { type: "unassigned", belongsIn: "Server Rack B" },
				},
			],
			bounds: [
				{ x: 0.1, y: 0.1 },
				{ x: 0.2, y: 0.1 },
				{ x: 0.2, y: 0.2 },
				{ x: 0.1, y: 0.2 },
			],
		},
		{
			id: "c2",
			name: "Server Rack B",
			assignments: new Set(["a3", "a4"]),
			assets: [
				{
					id: "a2",
					name: "UPS",
					status: { type: "unassigned", belongsIn: "Server Rack A" },
				},
				{
					id: "a3",
					name: "Firewall",
					status: { type: "missing", foundIn: "Server Rack A" },
				},
				{
					id: "a4",
					name: "KVM",
					status: { type: "missing", foundIn: null },
				},
			],
			bounds: [
				{ x: 0.3, y: 0.1 },
				{ x: 0.4, y: 0.1 },
				{ x: 0.4, y: 0.2 },
				{ x: 0.3, y: 0.2 },
			],
		},
	],
};

export function inventoryZoneToState(zone: InventoryZone): InventoryState {
	//TODO: Derive inventory state from zone and scan results
	return INVENTORY_STATE;
}
